import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "../App";
import { Shield, Download, Trash2, AlertTriangle, CheckCircle2, Clock, ArrowLeft, FileJson } from "lucide-react";

export default function PrivacyData() {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [confirm, setConfirm] = useState({ open: false, immediate: false });
  const [phrase, setPhrase] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = () => axios.get(`${API}/me/deletion-status`).then(r => setStatus(r.data)).catch(() => setStatus(null));
  useEffect(() => { load(); }, []);

  const exportData = async () => {
    setBusy(true); setErr("");
    try {
      const r = await axios.get(`${API}/me/data-export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `petbond-data-export-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setMsg("Your data export was downloaded.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not export your data.");
    } finally {
      setBusy(false);
    }
  };

  const submitDelete = async () => {
    setBusy(true); setErr("");
    try {
      const body = confirm.immediate
        ? { immediate: true, reason, confirm_phrase: phrase }
        : { immediate: false, reason };
      const r = await axios.post(`${API}/me/delete-account`, body);
      if (r.data.mode === "immediate") {
        setMsg("Your account and personal data have been deleted. You'll be signed out shortly.");
        setTimeout(() => { window.location.href = "/"; }, 2500);
      } else {
        setMsg(`Deletion scheduled for ${new Date(r.data.scheduled_at).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}. You can cancel anytime in the next ${r.data.grace_days} days.`);
        load();
      }
      setConfirm({ open: false, immediate: false });
      setPhrase(""); setReason("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not process the request.");
    } finally {
      setBusy(false);
    }
  };

  const cancelDeletion = async () => {
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/me/delete-account/cancel`);
      setMsg("Deletion cancelled. Your account is active.");
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not cancel.");
    } finally {
      setBusy(false);
    }
  };

  if (!user) return <div className="p-8">Please sign in.</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="privacy-data-page">
      <Link to="/app/profile" className="inline-flex items-center gap-1 text-sm text-ink-soft hover:text-brand"><ArrowLeft className="w-4 h-4"/> Back to profile</Link>

      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold inline-flex items-center gap-1.5"><Shield className="w-4 h-4"/> Your data, your rights</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">Privacy &amp; Data</h1>
        <p className="text-ink-soft text-sm max-w-2xl mt-1">Under the <strong>Digital Personal Data Protection Act 2023 (India)</strong> and the EU GDPR, you have the right to access a copy of your personal data and to request that we delete it. Both are available below — no support ticket required.</p>
      </header>

      {/* Pending deletion banner */}
      {status?.pending && (
        <div className="rounded-2xl bg-danger/10 border border-danger px-4 py-4 flex items-start gap-3" data-testid="pending-deletion-banner">
          <AlertTriangle className="w-5 h-5 text-danger shrink-0 mt-0.5"/>
          <div className="flex-1">
            <div className="font-bold text-ink">Account deletion scheduled</div>
            <p className="text-sm text-ink-soft mt-0.5">Your account will be permanently deleted on <strong>{new Date(status.scheduled_at).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}</strong> — <strong>{status.days_left} day{status.days_left === 1 ? "" : "s"}</strong> remaining. You can cancel and keep using PetBond anytime before then.</p>
            <button data-testid="cancel-deletion-btn" onClick={cancelDeletion} disabled={busy} className="mt-3 bg-ink text-white rounded-full px-4 py-2 text-sm font-bold hover:bg-brand disabled:opacity-60">Cancel deletion</button>
          </div>
        </div>
      )}

      {msg && <div className="rounded-2xl bg-leaf/20 border border-leaf px-4 py-3 text-sm font-bold inline-flex items-start gap-2" data-testid="success-msg"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0"/> {msg}</div>}
      {err && <div className="rounded-2xl bg-danger/10 border border-danger px-4 py-3 text-sm font-bold text-danger inline-flex items-start gap-2" data-testid="error-msg"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/> {err}</div>}

      {/* Right to Access — Data Export */}
      <section className="bg-surface border border-line rounded-3xl p-6 shadow-soft" data-testid="data-export-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-sky/15 grid place-items-center shrink-0"><FileJson className="w-6 h-6 text-sky"/></div>
          <div className="flex-1">
            <h2 className="font-heading text-xl font-extrabold">Download a copy of your data</h2>
            <p className="text-sm text-ink-soft mt-1">A JSON file containing everything we store linked to your account — pets, bookings, orders, prescriptions, chat history, subscriptions, donations and more.</p>
            <ul className="text-xs text-ink-soft mt-3 space-y-1">
              <li>• Includes all 30+ data categories.</li>
              <li>• Machine-readable — portable to another service.</li>
              <li>• Generated on-demand. No retention after download.</li>
            </ul>
            <button data-testid="export-data-btn" onClick={exportData} disabled={busy} className="mt-4 bg-sky text-white rounded-full px-5 py-2.5 text-sm font-bold inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-60">
              <Download className="w-4 h-4"/> {busy ? "Preparing…" : "Download my data (JSON)"}
            </button>
          </div>
        </div>
      </section>

      {/* Right to Erasure — Account Deletion */}
      {!status?.pending && (
        <section className="bg-surface border border-danger/40 rounded-3xl p-6 shadow-soft" data-testid="delete-account-card">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-danger/15 grid place-items-center shrink-0"><Trash2 className="w-6 h-6 text-danger"/></div>
            <div className="flex-1">
              <h2 className="font-heading text-xl font-extrabold">Delete my account</h2>
              <p className="text-sm text-ink-soft mt-1">Permanently remove your account and all personal data from PetBond. Financial records (orders, invoices, tax receipts) are <strong>anonymised</strong> rather than deleted to satisfy Indian Income Tax / GST 6-year retention requirements — your name, email and phone are stripped, but transaction amounts remain in our books.</p>

              <div className="grid sm:grid-cols-2 gap-3 mt-5">
                <div className="rounded-2xl border border-line p-4">
                  <div className="font-bold text-sm inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-brand"/> 30-day grace period</div>
                  <p className="text-xs text-ink-soft mt-1">Account is locked immediately; data is purged after 30 days. You can change your mind during the grace window.</p>
                  <button data-testid="schedule-delete-btn" onClick={() => setConfirm({ open: true, immediate: false })} className="mt-3 w-full bg-ink text-white rounded-full py-2 text-sm font-bold hover:bg-brand">Schedule deletion (recommended)</button>
                </div>
                <div className="rounded-2xl border border-danger/40 p-4 bg-danger/5">
                  <div className="font-bold text-sm inline-flex items-center gap-1.5 text-danger"><AlertTriangle className="w-4 h-4"/> Delete immediately</div>
                  <p className="text-xs text-ink-soft mt-1">Irreversible. Personal data is wiped now, transaction records are anonymised. You will be signed out.</p>
                  <button data-testid="immediate-delete-btn" onClick={() => setConfirm({ open: true, immediate: true })} className="mt-3 w-full bg-danger text-white rounded-full py-2 text-sm font-bold hover:opacity-90">Delete now (irreversible)</button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Confirm modal */}
      {confirm.open && (
        <div className="fixed inset-0 z-50 bg-black/55 grid place-items-center p-4" onClick={() => !busy && setConfirm({ open: false, immediate: false })}>
          <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md border border-line shadow-soft" data-testid="delete-confirm-modal">
            <h3 className="font-heading text-xl font-extrabold flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${confirm.immediate ? "text-danger" : "text-brand"}`}/>
              {confirm.immediate ? "Delete account immediately?" : "Schedule deletion in 30 days?"}
            </h3>
            <p className="text-sm text-ink-soft mt-2">
              {confirm.immediate
                ? "This will permanently delete your pets, bookings, prescriptions, chat history, sponsorships, and revoke all sessions. Financial records will be anonymised. You will be signed out."
                : "Your account will be locked immediately. After 30 days the data will be permanently deleted. You can cancel anytime during the grace window."}
            </p>

            <label className="block mt-4">
              <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">Reason (optional — helps us improve)</span>
              <textarea data-testid="reason-input" value={reason} onChange={e => setReason(e.target.value)} rows={2} className="w-full mt-1 border border-line rounded-2xl px-3 py-2 text-sm bg-surface focus:outline-none focus:border-brand" placeholder="Found a better service / not using anymore / privacy concern …"/>
            </label>

            {confirm.immediate && (
              <label className="block mt-4">
                <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">Type <span className="font-mono bg-danger/10 text-danger px-1 rounded">DELETE MY ACCOUNT</span> to confirm</span>
                <input data-testid="confirm-phrase" autoFocus value={phrase} onChange={e => setPhrase(e.target.value)} className="w-full mt-1 border border-danger/50 rounded-full px-4 py-2 text-sm font-mono bg-surface focus:outline-none focus:border-danger" placeholder="DELETE MY ACCOUNT"/>
              </label>
            )}

            <div className="flex gap-2 mt-5">
              <button onClick={() => setConfirm({ open: false, immediate: false })} disabled={busy} className="flex-1 bg-surface-muted rounded-full py-2.5 text-sm font-bold hover:bg-line">Cancel</button>
              <button
                data-testid="confirm-delete-btn"
                disabled={busy || (confirm.immediate && phrase.trim().toUpperCase() !== "DELETE MY ACCOUNT")}
                onClick={submitDelete}
                className={`flex-1 rounded-full py-2.5 text-sm font-bold text-white disabled:opacity-50 ${confirm.immediate ? "bg-danger" : "bg-ink hover:bg-brand"}`}
              >
                {busy ? "Working…" : (confirm.immediate ? "Yes, delete now" : "Schedule deletion")}
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="text-xs text-ink-soft bg-surface-muted rounded-2xl p-4">
        Questions about your data? Read our <Link to="/privacy" className="text-brand font-bold hover:underline">Privacy Policy</Link> or email <a href="mailto:privacy@petbond.in" className="text-brand font-bold hover:underline">privacy@petbond.in</a>. We respond within 7 working days, as required under DPDP Act §11.
      </footer>
    </div>
  );
}
